import { Tag, type TableColumnsType } from 'antd';
import { CatalogueRowActions } from '../../../shared/components/CatalogueRowActions';
import type { BaseListQuery, ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { DirectoryTagView } from '../features/directory-tags/directory-tags.api';
import { tagLabel } from '../policies/tag-labels';

interface DirectoryRow {
  id: string;
  name: string;
  phone: string | null;
  isActive: boolean;
}

interface PlaceColumn<V> {
  title: string;
  value: (row: V) => string;
  /** The sort field in the screen's list query; absent when the screen cannot sort by it. */
  sortKey?: string;
}

interface TagsColumn<V> {
  title: string;
  /** The sort/filter key in the screen's list query, e.g. `serviceType` or `category`. */
  filterKey: string;
  /** The catalogue: names for the codes a row holds, and (active entries) the filter dropdown's choices. */
  catalogue: DirectoryTagView[];
  values: (row: V) => string[];
}

interface BuildDirectoryColumnsArgs<Q extends BaseListQuery, V extends DirectoryRow> {
  list: Pick<ListQueryControls<Q>, 'sortOrderFor' | 'filteredValueFor'>;
  /** A supplier's free-text location, or the zone a garage sits in. */
  place: PlaceColumn<V>;
  tags: TagsColumn<V>;
  /** Without it the actions column is left out: `maintenance.view` alone reads the directory. */
  actions?: {
    onEdit: (row: V) => void;
    onDeactivate: (row: V) => Promise<void>;
    onReactivate: (row: V) => Promise<void>;
  };
}

/** Name, place, phone, tags and status — the shape a garage and a supplier row share. Each column's `key` is its sort field or filter param. */
export function buildDirectoryColumns<Q extends BaseListQuery, V extends DirectoryRow>({
  list,
  place,
  tags,
  actions,
}: BuildDirectoryColumnsArgs<Q, V>): TableColumnsType<V> {
  const columns: TableColumnsType<V> = [
    { title: 'Name', dataIndex: 'name', key: 'name', sorter: true, sortOrder: list.sortOrderFor('name' as Q['sort']) },
    {
      title: place.title,
      key: place.sortKey ?? 'place',
      render: (_, row) => place.value(row),
      ...(place.sortKey ? { sorter: true, sortOrder: list.sortOrderFor(place.sortKey as Q['sort']) } : {}),
    },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (phone: string | null) => phone ?? <span className="text-muted">—</span> },
    {
      title: tags.title,
      key: tags.filterKey,
      filters: tags.catalogue.filter((tag) => tag.isActive).map((tag) => ({ text: tag.name, value: tag.code })),
      filteredValue: list.filteredValueFor(tags.filterKey as keyof Q & string),
      render: (_, row) => (
        <div className="flex flex-wrap gap-1">
          {tags.values(row).map((value) => (
            <Tag key={value}>{tagLabel(tags.catalogue, value)}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'status',
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      filteredValue: list.filteredValueFor('status' as keyof Q & string),
      render: (isActive: boolean) => <Tag color={isActive ? 'success' : 'default'}>{isActive ? 'Active' : 'Inactive'}</Tag>,
    },
  ];
  if (actions) columns.push({ title: '', key: 'actions', render: (_, row) => <CatalogueRowActions row={row} {...actions} /> });
  return columns;
}
